import React, { FC, useContext } from 'react';

import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import {
  Card,
  CardAccompanyingInfo,
  CardDescription,
  CardDurationInfo,
  CardGragient,
  CardHeader,
  CardImageLabel,
  CardImg,
  CardInfo,
  CardLabel,
  CardLabels,
  CardPlayIcon,
  CardProgressLabel,
  CardRepeatIcon,
} from '../../../../components/Card';
import CardPathwayActionBlock from '../../../../components/Card/components/CardPathwayActionBlock/CardPathwayActionBlock';
import ToggleFromList from '../../../../components/ToggleFromList/ToggleFromList';
import { CategoryLabel } from '../../../../components/UI/CategoryLabel';
import CategoryLabelWrapper from '../../../../components/UI/CategoryLabel/CategoryLabelWrapper';
import DifficultyIcon from '../../../../components/UI/DifficultyIcon';
import { ProgressLine } from '../../../../components/UI/ProgressLine';
import { DEFAULT_DISCOVER_URL } from '../../../../const';
import { UserInfoCtx } from '../../../../containers/CommonLayout/CommonLayout';
import theme, { colorsPalette } from '../../../../theme';
import { DashVideo, DiscoverPathwaysDetailedInfo } from '../../../../types';
import { WIDE_CATEGORY_LABEL_WIDTH } from '../../../../utils/const';
import {
  calcVideoProgressAndLinkToShow,
  createCategoryLink,
  formatDateToNewsFormat,
  secondsToHHHmmm,
  secondsToHms,
} from '../../../../utils/helpers';
import useToggleFromWatchlist from '../../../../utils/hooks/useToggleFromWatchlist';
import Ratio from '../../../CourseProgress/components/Ratio';
import { HITS_TYPES, SendHitsEvent } from '../../const';
import { sendAlgoliaClickEvent } from '../../helpers';
import { GlossaryHit } from '../GlossaryHit';
import useStyles from './styles';

const getCategoriesArray = (item, isGridHit = false) => {
  const res: React.ReactNode[] = [];

  if (item.searchable_type === HITS_TYPES.VIDEO || item.searchable_type === HITS_TYPES.PATHWAY) {
    if (item?.categories_array) {
      const filteredCategoriesArr = item.categories_array.filter((item) => item?.id && item?.title);

      if (isGridHit) {
        const categoryLink = createCategoryLink(filteredCategoriesArr?.[0]);
        filteredCategoriesArr.length &&
          res.push(
            <CategoryLabel
              key={filteredCategoriesArr[0]?.id}
              name={filteredCategoriesArr[0]?.title}
              textMaxWidth={WIDE_CATEGORY_LABEL_WIDTH}
              url={categoryLink}
            />,
          );
      } else {
        filteredCategoriesArr.forEach((cat) => {
          const categoryLink = createCategoryLink(cat);
          res.push(
            <CategoryLabel
              key={cat?.id}
              name={cat?.title}
              url={categoryLink}
              textMaxWidth={WIDE_CATEGORY_LABEL_WIDTH}
            />,
          );
        });
      }
    }
    if (item?.difficulty) {
      res.push(<DifficultyIcon key="difficulty" difficulty={item?.difficulty} />);
    }
  }

  return res;
};

export const getGeneralizedObject = (item, videoProgress?: DashVideo | null, isGridHit = false) => {
  const categories = getCategoriesArray(item, isGridHit);
  switch (item.searchable_type) {
    case HITS_TYPES.VIDEO: {
      const { linkToShow, progressToShow } = calcVideoProgressAndLinkToShow(
        videoProgress?.completed,
        videoProgress?.last_position,
        item?.duration,
        item?.video_url,
        videoProgress?.chapter,
        videoProgress?.time,
      );

      return {
        id: item?.id,
        type: item?.type,
        title: item?.title,
        description: item?.description,
        preview_url: item?.preview_url,
        user: item?.user,
        userLink: item?.user_slug && `${DEFAULT_DISCOVER_URL}/experts/${item?.user_slug}`,
        duration: secondsToHms(item?.duration),
        searchableType: item.searchable_type,
        categoriesArr: categories,
        link: linkToShow,
        progress: progressToShow,
        isReactLink: true,
      };
    }
    case HITS_TYPES.PATHWAY: {
      return {
        id: item?.id,
        type: item?.type,
        title: item.name,
        preview_url: item?.image_url,
        description: item.description,
        count_videos: item?.count_videos,
        accompanyingText:
          `${item?.count_videos} ${item?.count_videos > 1 ? 'videos' : 'video'}` +
          ' • ' +
          secondsToHHHmmm(item?.duration),
        searchableType: item.searchable_type,
        categoriesArr: categories,
        categories: item?.categories_array,
        link: `/pathway/${item.slug}`,
        isReactLink: true,
        slug: item.slug,
        cpdCredits: item?.cpd_credits,
      };
    }
    case HITS_TYPES.NEWS: {
      return {
        id: item?.id,
        type: item?.type,
        title: item.title,
        preview_url: item?.image_url,
        description: item.post_introduction,
        accompanyingText: formatDateToNewsFormat(item?.published_at) + ' • ' + item?.user,
        searchableType: item.searchable_type,
        categoriesArr: categories,
        link: `/news/${item.id}`,
        isReactLink: true,
      };
    }
    default:
      return null;
  }
};

interface HitProps {
  hit: any;
  pathwayProgress?: DiscoverPathwaysDetailedInfo | null;
  videoProgress?: DashVideo | null;
  tenantTitle?: string | null;
  tenantName?: string | null;
  isInWatchlist?: boolean;
  sendEvent: SendHitsEvent;
}

const Hit: FC<HitProps> = ({
  hit,
  pathwayProgress,
  videoProgress,
  tenantTitle,
  tenantName,
  isInWatchlist = false,
  sendEvent,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const data = getGeneralizedObject(hit, videoProgress);

  const { userInfo } = useContext(UserInfoCtx);
  const [toggleFromWatchlist, toggleFromWatchlistLoading] = useToggleFromWatchlist(
    isInWatchlist,
    data?.id,
  );

  const handleClickHit = () => {
    sendAlgoliaClickEvent(sendEvent, hit);
  };

  if (hit.searchable_type === HITS_TYPES.GLOSSARY) {
    return (
      <GlossaryHit
        slug={hit?.slug}
        title={hit?.title}
        description={hit?.definition}
        categories={hit?.categories_array}
        videoUrl={hit?.video_url}
        handleClickHit={handleClickHit}
      />
    );
  }

  const hasPathwayProgress =
    data?.searchableType === HITS_TYPES.PATHWAY &&
    !!pathwayProgress?.count_watched_videos &&
    !!data?.count_videos;
  const hash =
    pathwayProgress?.passed_quizes && pathwayProgress.passed_quizes[0]
      ? pathwayProgress.passed_quizes[0]?.hash
      : undefined;
  const passDate =
    pathwayProgress?.passed_quizes && pathwayProgress.passed_quizes[0]
      ? pathwayProgress.passed_quizes[0]?.date_passing
      : undefined;
  const testId =
    pathwayProgress?.not_passed_quizes && pathwayProgress.not_passed_quizes[0]
      ? pathwayProgress.not_passed_quizes[0]?.id
      : undefined;
  const hasUncompletedQuizes = Boolean(pathwayProgress?.not_passed_quizes?.length);

  return (
    <>
      <Card wrapperClass={hasPathwayProgress ? classes.pathwayCard : ''}>
        <Ratio ratio={68} className={classes.imgLinkWrapper}>
          <Link className={classes.imgLink} to={data?.link} onClick={handleClickHit}>
            <CardImg
              wrapperClass={cn(
                classes.imageWrapper,
                videoProgress?.completed ? classes.imageBackground : '',
              )}
              imgPath={data?.preview_url}
            >
              {data?.searchableType === HITS_TYPES.VIDEO && (
                <>
                  {videoProgress?.completed ? <CardRepeatIcon /> : <CardPlayIcon />}
                  {videoProgress?.completed && <CardLabel>{t('page.pathway.completed')}</CardLabel>}
                  {data?.progress && <ProgressLine width={data.progress} />}
                </>
              )}
              {data?.searchableType === HITS_TYPES.PATHWAY && (
                <>
                  <CardGragient />
                  <CardImageLabel
                    name={t('page.discover.pathway')}
                    bgColor={colorsPalette.pink}
                    textColor={theme.palette.secondary.dark}
                  />
                  {pathwayProgress?.count_watched_videos && data?.count_videos && (
                    <>
                      {(hash || !testId) &&
                      pathwayProgress.count_watched_videos === data.count_videos ? (
                        <CardLabel>
                          {hasUncompletedQuizes
                            ? t('page.pathway.watched')
                            : t('page.pathway.completed')}
                        </CardLabel>
                      ) : (
                        <CardProgressLabel
                          current={pathwayProgress.count_watched_videos}
                          total={data.count_videos}
                        />
                      )}
                      <ProgressLine
                        width={(pathwayProgress.count_watched_videos * 100) / data.count_videos}
                      />
                    </>
                  )}
                </>
              )}
              {data?.searchableType === HITS_TYPES.NEWS && (
                <CardImageLabel name={t('page.discover.news')} bgColor={colorsPalette.blueLight} />
              )}
            </CardImg>
          </Link>
        </Ratio>
        <CardInfo>
          <div className={classes.labelsWrapper}>
            {data?.categoriesArr && data?.categoriesArr.length > 0 && (
              <CardLabels wrapperClass={classes.cardLabels}>{data.categoriesArr}</CardLabels>
            )}
            {!!data?.cpdCredits && (
              <CategoryLabelWrapper
                bgColor={colorsPalette.violetAnalyticsCard}
                wrapperClass={classes.cpdLabel}
              >
                <div className={classes.cpdScore}>{data.cpdCredits}</div>
                <div className={classes.cpdTitle}>{t('common.cpdCredits')}</div>
              </CategoryLabelWrapper>
            )}
          </div>
          {data?.title && (
            <Link className={classes.titleWrapper} to={data?.link} onClick={handleClickHit}>
              <CardHeader text={data.title} />
            </Link>
          )}
          {data?.description && <CardDescription text={data.description} />}
          <div className={classes.infoRow}>
            {data?.accompanyingText ? (
              <CardAccompanyingInfo text={data.accompanyingText} />
            ) : data?.user || data?.duration ? (
              <CardDurationInfo
                durationName={data?.user}
                duration={data?.duration}
                link={data?.userLink}
                reverse={data?.searchableType === HITS_TYPES.NEWS}
              />
            ) : null}
            {data?.searchableType === HITS_TYPES.VIDEO && userInfo && (
              <ToggleFromList
                disabled={toggleFromWatchlistLoading}
                isAdd={!isInWatchlist}
                onClick={toggleFromWatchlist}
              />
            )}
          </div>
        </CardInfo>
      </Card>
      {data?.searchableType === HITS_TYPES.PATHWAY &&
        !!pathwayProgress?.count_watched_videos &&
        !!data?.count_videos && (
          <CardPathwayActionBlock
            category={data?.categories?.[0]?.title}
            wrapperClass={classes.pathwayActionBlock}
            pathwaySlug={data?.slug}
            totalVideos={data.count_videos}
            watchedVideos={pathwayProgress.count_watched_videos}
            hash={hash}
            testId={testId}
            passDate={passDate}
            tenantTitle={tenantTitle}
            tenantName={tenantName}
            pathwayName={data?.title}
            cpdCredits={data?.cpdCredits}
          />
        )}
    </>
  );
};

export default Hit;
